import { Ref, useEffect, useImperativeHandle } from 'react';
import { Autocomplete } from 'common/components/Inputs/Autocomplete/Autocomplete';

import { useTagsAutocompleteInput } from 'tags/hooks/useTagsAutocompleteInput';
import { useTagsAssign } from 'tags/hooks/useTagsSave';
import { useTranslation } from 'react-i18next';
import { Tag } from 'src/tags/models/tags';

export type TagsAutoCompleteInputRefProps = {
  saveTags: () => Promise<void>;
  resetTags: () => void;
};

export type TagsAutoCompleteInputProps = {
  id: string;
  tags: Tag[];
  ref: Ref<TagsAutoCompleteInputRefProps>;
  onTagsDirtyChange: (dirty: boolean) => void;
  onTagsChange: (data: { cardId: string; tags: Tag[] }) => void;
};

export const TagsAutoCompleteInput = ({
  id,
  tags,
  ref,
  onTagsDirtyChange,
  onTagsChange,
}: TagsAutoCompleteInputProps) => {
  const { t } = useTranslation();

  const { mutateAsync: saveTagsMutation } = useTagsAssign(id, (tags) => {
    onTagsChange({ cardId: id, tags });
  });

  const {
    options,
    selectedTagsOptions,
    create,
    update,
    search,
    createdOptions,
    reset: resetTags,
    changed: tagsChanged,
    isSearching,
    errors,
    hasNextPage,
    fetchNextPage,
  } = useTagsAutocompleteInput({
    initialAssignedTags: tags || [],
  });

  useEffect(() => {
    onTagsDirtyChange(tagsChanged);
  }, [tagsChanged, onTagsDirtyChange]);

  const saveTags = () =>
    saveTagsMutation({
      createdTagIds: createdOptions.map(({ id }) => id),
      tagsToAssign: selectedTagsOptions,
    });

  useImperativeHandle(
    ref,
    () => ({
      resetTags,
      saveTags,
    }),
    [tagsChanged, saveTags],
  );

  return (
    <Autocomplete
      group={createdOptions.length > 0}
      loading={isSearching}
      variant="outlined"
      data-testid="tags-multiselect"
      value={selectedTagsOptions}
      options={options}
      creatable={true}
      creatableMessage={t('common.confirmToAddTag')}
      onSearch={search}
      onCreate={create}
      onChange={update}
      errors={errors}
      hasMore={hasNextPage}
      onMoreShouldBeLoad={() => {
        fetchNextPage();
      }}
    />
  );
};
